import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Json } from '@/integrations/supabase/types';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

export interface LinkedInMention {
  urn: string;
  name: string;
  vanityName: string;
  logoUrl: string | null;
}

export type LinkedInLookupResult =
  | { state: 'found'; mention: LinkedInMention }
  | { state: 'not_found' }
  | { state: 'invalid_link' }
  | { state: 'not_connected' };

const ORGANIZATION_URN = /^urn:li:organization:\d+$/;

export function parseStoredMentions(value: unknown): LinkedInMention[] {
  if (!Array.isArray(value)) return [];
  const out: LinkedInMention[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const m = item as Record<string, unknown>;
    if (typeof m.urn !== 'string' || !ORGANIZATION_URN.test(m.urn)) continue;
    if (typeof m.name !== 'string' || !m.name) continue;
    out.push({
      urn: m.urn,
      name: m.name,
      vanityName: typeof m.vanityName === 'string' ? m.vanityName : '',
      logoUrl: typeof m.logoUrl === 'string' && m.logoUrl.startsWith('https://') ? m.logoUrl : null,
    });
  }
  return out;
}

class LinkedInTagsService extends BaseService {
  private static instance: LinkedInTagsService;

  private constructor() {
    super({ defaultTTL: 0, enableCache: false, enableRetry: true });
  }

  static getInstance(): LinkedInTagsService {
    if (!LinkedInTagsService.instance) LinkedInTagsService.instance = new LinkedInTagsService();
    return LinkedInTagsService.instance;
  }

  async lookupCompany(
    url: string,
    connectionId?: string,
  ): Promise<ServiceResponse<LinkedInLookupResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client.functions.invoke<LinkedInLookupResult>(
        'linkedin-company-lookup',
        { body: { url, ...(connectionId ? { connectionId } : {}) } },
      );
      if (error) throw error;
      if (!data || typeof data !== 'object' || !('state' in data)) {
        throw new Error('LinkedIn lookup returned an unexpected response');
      }
      if (data.state === 'found') {
        const [mention] = parseStoredMentions([data.mention]);
        if (!mention) throw new Error('LinkedIn lookup returned an unusable company');
        return { state: 'found', mention };
      }
      return data;
    }, 'Failed to look up the LinkedIn company page');
  }

  async savePostMentions(
    postId: string,
    mentions: LinkedInMention[],
  ): Promise<ServiceResponse<LinkedInMention[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { error } = await this.client
        .from('social_posts')
        .update({ linkedin_mentions: mentions as unknown as Json })
        .eq('id', postId);
      if (error) throw error;
      return mentions;
    }, 'Failed to save LinkedIn tags');
  }

  async getPostMentions(postId: string): Promise<ServiceResponse<LinkedInMention[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('social_posts')
        .select('linkedin_mentions')
        .eq('id', postId)
        .maybeSingle();
      if (error) throw error;
      return parseStoredMentions(data?.linkedin_mentions);
    }, 'Failed to load LinkedIn tags');
  }

  async getRecentMentions(limit = 50): Promise<ServiceResponse<LinkedInMention[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const ownerId = await resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .select('linkedin_mentions')
        .eq('user_id', ownerId)
        .neq('linkedin_mentions', '[]')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      const seen = new Set<string>();
      const out: LinkedInMention[] = [];
      for (const row of data ?? []) {
        for (const m of parseStoredMentions(row.linkedin_mentions)) {
          if (seen.has(m.urn)) continue;
          seen.add(m.urn);
          out.push(m);
        }
      }
      return out;
    }, 'Failed to load recently tagged companies');
  }
}

export const linkedInTagsService = LinkedInTagsService.getInstance();
